export const DefaultEntryPage: string = "/app/school/announcements/";
declare var hexMD5_: (src: string) => string;

import { LoginRepository, LoginResult, LoginType, IdpAccountInfo, IdentityProvider, GetMobileInfoResponse } from "../login.repository";
import { LongWorkService } from "approot/global/providers/uikit.dialogs";
import { postTo } from "webroot/static/src/common/js/common.navigation";
import { LoginDataService } from "./login.data.service";

export interface LoginOptions {
	entryPage?: string;
	processing?: boolean;
	saveAccount?: boolean;
	redirectUri?: string;
	clientId?: string;
	scope?: string;
	state?: string;
	codeChallenge?: string;
	codeChallengeMethod?: string;
	acrValues?: string;

	mobile?: boolean;
	mobileEntry?: boolean;
	mobileAccessToken?: string;
	responseType?: string;

	idpBindUser?: boolean;
	loginState?: string;
}

export interface ServAdminLoginOptions {
	loginType?: LoginType,
	lscope?: string,
	grantType?: string,
	idp?: string,
	loginState?: string
}

export interface FixedLoginInfo {
	loginName?: string;
	password?: string;
	schoolId?: number;
	emId?: number;
}

export enum RoleGroup {
	Staffs = 0,
	Students = 1,
	Parents = 2,
	EducManagers = 3
}

export enum FixedEsiaRole {
	Students = 1,
	Parents = 2,
	AllStaffs = 3
}

export class LoginService {
	private defaultOptions: LoginOptions = {
		processing: true,
		saveAccount: false
	}

	public accountInfo
	public mobileInfo: GetMobileInfoResponse

	/*@ngInject*/
	constructor(
		private loginRepository: LoginRepository,
		private loginDataService: LoginDataService,
		private $longWork: LongWorkService
	) {

	}

	getMobileInfo (loginState?: string): Promise<GetMobileInfoResponse> {
		if (this.mobileInfo) {
			return Promise.resolve(this.mobileInfo)
		} else {
			return this.loginRepository.getMobileInfo(loginState).then((mobileInfo: GetMobileInfoResponse) => {
				if (mobileInfo) {
					this.mobileInfo = mobileInfo
				} else {
					this.mobileInfo = {
						isMobile: false,
						mobileBind: false
					}
				}

				return this.mobileInfo
			})
		}
	}

	login(loginName: string, password: string, schoolId?: number, emId?: number, options?: LoginOptions, addOpts?: any): Promise<void> {
		let loginOptions = Object.assign({}, this.defaultOptions, options);

		if (loginOptions.processing) {
			this.$longWork.show();
		}

		return this.loginRepository.getAuthData()
			.then((authData) => {
				let pw2 = hexMD5_(authData.salt + hexMD5_(password));

				let authParams: any = Object.assign({}, {
					lt: authData.lt,
					pw2: pw2,
					ver: authData.ver,
					un: loginName
				}, addOpts);

				if (schoolId > 0) {
					authParams.scid = schoolId;
					authParams.loginType = LoginType.School;
				}
				else if (emId > 0) {
					authParams.emid = emId;
					authParams.loginType = LoginType.EducManager;
				}

				this.prepareAuthData(authParams, options);

				return this.loginRepository.auth(authParams);
			})
			.then((loginResult) => this.processLoginResult(loginResult, loginOptions))
			.then((loginResult) => this.goToEntryPage(loginResult, loginOptions))
			.catch((exception) => {
				console.log(exception);

				if (loginOptions.processing) {
					this.$longWork.close();
				}
				return Promise.reject("error");
			})
	}

	loginWithIdp(idp: string, userId: string, role?: number, options?: LoginOptions, fixedLogin?: FixedLoginInfo): Promise<any> {
		let loginOptions = Object.assign({}, this.defaultOptions, options);

		if (loginOptions.processing) {
			this.$longWork.show();
		}

		let authParams: any = {
			loginType: LoginType.Idp,
			lscope: userId,
			idp: idp
		};

		this.prepareAuthData(authParams, options);

		if (this.checkRole(role)) {
			authParams.rolegroup = role;
		}

		if (fixedLogin) {
			return this.login(fixedLogin.loginName, fixedLogin.password, fixedLogin.schoolId, fixedLogin.emId, loginOptions, authParams);
		} else {
			return this.loginRepository.auth(authParams)
				.then((loginResult) => this.processLoginResult(loginResult, loginOptions))
				.then((loginResult) => this.goToEntryPage(loginResult, loginOptions))
				.catch((exception) => {
					console.log(exception);

					if (loginOptions.processing) {
						this.$longWork.close();
					}
					return Promise.reject("error");
				});
		}
	}

	getIdpAccountInfo(idp: string, loginState: string): Promise<IdpAccountInfo> {
		return this.loginRepository.getIdpAccountInfo(idp, loginState)
			.then(accountInfo => {
				this.accountInfo = accountInfo
				return accountInfo
			});
	}

	private checkRole(role?: number): boolean {
		return role != null && role != undefined;
	}

	loginWithRefreshToken(refreshToken: string, options?: LoginOptions, addOpts?: any) {
		let loginOptions = Object.assign({}, this.defaultOptions, options);

		this.$longWork.show();

		let authParams: any = Object.assign({}, {
			grant_type: "refresh_token",
			refresh_token: refreshToken,
			LoginType: LoginType.Refresh
		}, addOpts);

		if (options?.clientId) {
			authParams.client_id = options.clientId;
		}
		if (options?.responseType) {
			authParams.response_type = options.responseType;
		}
		if (options?.redirectUri) {
			authParams.redirect_uri = options.redirectUri;
		}

		this.prepareAuthData(authParams, options);

		return this.loginRepository.raw.auth(authParams)
			.then((loginResult) => this.processLoginResult(loginResult, loginOptions))
			.then((loginResult) => this.goToEntryPage(loginResult, loginOptions))
			.catch((error) => {
				this.$longWork.close();
				return Promise.reject(error);
			});
	}

	servAdminLoginWithIdp(lscope: string, idp: string, password: string, loginState: string) {
		let loginOptions = { loginType: LoginType.Idp, lscope: lscope, grantType: "serv_admin_idp", idp, loginState };
		let loginPromise = this.servAdminLogin(password, loginOptions);

		return loginPromise;
	}

	prepareServAdminAuthParams(authParams, options: ServAdminLoginOptions) {
		if (options.loginType) {
			authParams.LoginType = options.loginType;
		}
		if (options?.lscope) {
			authParams.lscope = options.lscope;
		}
		if (options?.grantType) {
			authParams.grant_type = options.grantType;
		}
		if (options?.idp) {
			authParams.idp = options.idp;
		}
		if (options?.loginState) {
			authParams.loginState = options.loginState;
		}
	}

	servAdminLogin(password: string, options?: ServAdminLoginOptions): Promise<void> {
		this.$longWork.show();

		return this.loginRepository.getAuthData()
			.then((authData) => {
				let pw2 = hexMD5_(authData.salt + hexMD5_(password));

				let authParams: any = Object.assign({}, {
					lt: authData.lt,
					pw2: pw2,
					ver: authData.ver,
					LoginType: LoginType.ServAdmin
				});

				if (options) {
					this.prepareServAdminAuthParams(authParams, options);
				}



				return this.loginRepository.auth(authParams);
			})
			.then((loginResult) => this.goToEntryPage(loginResult))
			.catch((exception) => {
				console.log(exception);
				this.$longWork.close();

				return Promise.reject("error");
			})
	}

	private prepareAuthData(authParams: any, options?: LoginOptions) {
		if (options?.responseType) {
			authParams.response_type = options?.responseType;
		}
		if (options?.redirectUri) {
			authParams.redirect_uri = options?.redirectUri;
		}
		if (options?.scope) {
			authParams.scope = options?.scope;
		}
		if (options?.clientId) {
			authParams.client_id = options?.clientId;
		}
		if (options?.state) {
			authParams.state = options?.state;
		}
		if (options?.codeChallenge) {
			authParams.code_challenge = options.codeChallenge;
		}
		if (options?.codeChallengeMethod) {
			authParams.code_challenge_method = options.codeChallengeMethod;
		}
		if (options?.acrValues) {
			authParams.acr_values = options.acrValues;
		}
		if (options?.mobileEntry) {
			authParams.mobile = 1;
			if (options.mobileAccessToken) {
				authParams.mobileAccessToken = options.mobileAccessToken;
			}
		}
		if (options.idpBindUser) {
			authParams.idpBindUser = 1;
		}
		if (options.loginState) {
			authParams.loginState = options.loginState;
		}
	}

	private processLoginResult(loginResult: LoginResult, loginOptions?: LoginOptions) {
		let accountInfo = loginResult.accountInfo;
		if (!accountInfo) {
			return Promise.resolve(loginResult);
		}

		let hasRefreshToken = this.loginDataService.hasRefreshToken(accountInfo);

		if (loginOptions.saveAccount || hasRefreshToken) {
			accountInfo.accessToken = loginResult.accessToken;

			if (accountInfo.storeTokens) {
				let storedRefreshToken = {
					userId: accountInfo.user.id,
					orgId: accountInfo.currentOrganization.id,
					refreshToken: loginResult.refreshToken
				};

				this.loginDataService.storeRefreshToken(storedRefreshToken);
			} else {
				accountInfo.refreshToken = null;
				accountInfo.accessToken = null;
			}

			this.loginDataService.putAccount(accountInfo);
		}

		return Promise.resolve(loginResult);
	}

	private goToEntryPage(loginResult: LoginResult, loginOptions?: LoginOptions): Promise<any> {
		let path = loginOptions?.entryPage || loginResult.entryPoint;

		// todo. учёт необходимости выбора/уточнения роли для сотрудников-родителей

		if (loginOptions?.responseType == "code") {
			let data = {
				code: loginResult.code
			}

			postTo({ path: loginResult.entryPoint, params: loginResult.requestData, nocache: false, auth: false, method: "get" });
			return Promise.resolve(data);
		}

		if (loginResult.code) {
			let data = {
				code: loginResult.code
			};

			if (loginResult?.requestData?.state) {
				Object.assign(data, { state: loginResult.requestData.state });
			}

			path = loginResult.entryPoint;

			postTo({ path, params: data, nocache: false, auth: false, method: "get" });
			return Promise.resolve(data);
		}
		else {
			let requestData = loginResult.requestData;

			let data = Object.assign({}, {
				at: loginResult.at
			}, requestData);

			postTo({
				path,
				params: data,
				nocache: false,
				auth: false,
				method: this.isFrontendV2Path(path) ? "get" : "POST",
			});
			return Promise.resolve(data);
		}
	}

	private isFrontendV2Path(path: string): boolean {
		return /^\/app\/(?:v2|diary)(?:\/|$)/i.test(path);
	}
}
